import React, { useEffect, useState, useMemo } from 'react';
import { getRecords } from './api/airtable';
import { useNavigate } from 'react-router-dom';

export default function EmailTool() {
  const [allRecords, setAllRecords] = useState([]);
  const [recentBirthdays, setRecentBirthdays] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Fetch all records once and filter birthdays
  useEffect(() => {
    (async () => {
      const data = await getRecords();
      setAllRecords(data);
      const today = new Date();
      const oneMonthMs = 30 * 24 * 60 * 60 * 1000;

      const filteredBirthdays = data.filter(record => {
        const bdayStr = record.fields['Date of Birth (MM/DD/YYYY)'];
        if (!bdayStr) return false;
        const [month, day] = bdayStr.split('/');
        if (!month || !day) return false;

        const candidateDates = [
          new Date(today.getFullYear()    , parseInt(month) - 1, parseInt(day)),
          new Date(today.getFullYear() + 1, parseInt(month) - 1, parseInt(day)),
          new Date(today.getFullYear() - 1, parseInt(month) - 1, parseInt(day))
        ];

        return candidateDates.some(dt => Math.abs(today - dt) <= oneMonthMs);
      });

      setRecentBirthdays(filteredBirthdays);
    })();
  }, []);

  /** normalize for case-insensitive match */
  const n = (str = '') => str.toLowerCase();

  /** live-filtered search results */
  const filteredResults = useMemo(() => {
    const term = n(searchTerm.trim());
    if (!term) return [];
    return allRecords.filter(rec => n(rec.fields['Full Name']).includes(term));
  }, [allRecords, searchTerm]);

  const formatBirthday = (bdayStr) => {
    if (!bdayStr) return null;
    const [month, day] = bdayStr.split('/');
    const d = new Date(2000, parseInt(month) - 1, parseInt(day));
    const monthName = d.toLocaleString('default', { month: 'long' });
    return `${monthName} ${parseInt(day)}`;
  };

  return (
    <div className="p-8 min-h-screen bg-white font-sans">
      <h1 className="text-3xl font-bold text-[#8C1515] mb-6">Email Tool</h1>

      {/* SEARCH SECTION */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-2">Search for a Student</h2>
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            placeholder="Enter name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-grow max-w-md rounded border border-gray-300 p-2"
          />
          <button
            onClick={() => setSearchTerm('')}
            className="rounded bg-[#8C1515] px-4 py-2 text-white hover:bg-[#A6192E]"
          >
            Clear
          </button>
        </div>

        <div className="flex flex-wrap gap-4">
          {filteredResults.map(record => (
            <div key={record.id} className="w-80 rounded-lg border border-gray-300 p-4 shadow-md">
              <h2 className="mb-2 text-lg font-bold text-[#8C1515]">
                {record.fields['Full Name'] || 'Unnamed'}
              </h2>
              {record.fields['Date of Birth (MM/DD/YYYY)'] && (
                <p className="mb-3 text-sm">
                  <span className="font-semibold">Birthday:</span>{' '}
                  {formatBirthday(record.fields['Date of Birth (MM/DD/YYYY)'])}
                </p>
              )}
              <p className="mb-3 text-sm italic text-gray-600">Reason for Suggested Email: Custom Search</p>
              <button
                onClick={() => navigate(`/compose/${record.id}?reason=generic`)}
                className="inline-block rounded-md bg-[#8C1515] px-4 py-2 text-white hover:bg-[#A6192E] transition-colors"
              >
                Compose Email
              </button>
            </div>
          ))}

          {searchTerm.trim() && filteredResults.length === 0 && (
            <p className="mt-2 text-gray-500 italic">No students or alumni found with that name.</p>
          )}
        </div>
      </section>

      {/* BIRTHDAY SUGGESTIONS SECTION */}
      <section>
        <h2 className="text-xl font-semibold mb-2">Suggested Students to Email</h2>
        <div className="flex flex-wrap gap-4">
          {recentBirthdays.map(record => (
            <div key={record.id} className="w-80 rounded-lg border border-gray-300 p-4 shadow-md">
              <h2 className="mb-2 text-lg font-bold text-[#8C1515]">
                {record.fields['Full Name'] || 'Unnamed'}
              </h2>
              <p className="mb-3 text-sm">
                <span className="font-semibold">Reason for Suggested Email:</span> Birthday on {formatBirthday(record.fields['Date of Birth (MM/DD/YYYY)'])}
              </p>
              <button
                onClick={() => navigate(`/compose/${record.id}?reason=birthday`)}
                className="inline-block rounded-md bg-[#8C1515] px-4 py-2 text-white hover:bg-[#A6192E] transition-colors"
              >
                Compose Email
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
